// Libs
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useSnackbar } from "notistack";
import useLocalStorage from "use-local-storage";

// Services
import { GET, POST } from "./services/Bridge.js"
import ProtectedRoutes from "./services/ProtectedRoutes";

// Components
import Main from "./pages/Main/Main.jsx";
import AddNewItem from "./pages/AddNewItem/AddNewItem.jsx";
import MessageBox from "./pages/MessageBox/MessageBox.jsx";
import Edit from "./pages/Edit/Edit.jsx";
import Item from "./pages/Item/Item.jsx";
import Gdpr from "./pages/Gdpr/Gdpr.jsx"
import Contact from './pages/Contact/Contact.jsx';
import Login from "./pages/Login/Login.jsx";
import Register from "./pages/Register/Register.jsx";
import Loading from "./pages/Loading/Loading.jsx";
import NoPage from "./pages/noPage/noPage.jsx";
import Footer from "./pages/Footer/Footer.jsx"

// Languages
import slovakWebText from "./language/slovak.json";
import englishWebText from "./language/english.json";

// Style
import './default.css';

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#ce93d8',
    },
    secondary: {
      main: '#7a056d',
    },
    lemoneGreen: {
      main: '#00ADB5',
    },
    // You can define other colors like secondary, error, etc. here
  },
});

function App() {

  const { enqueueSnackbar } = useSnackbar();

  const [webTheme, setWebTheme] = useState(localStorage.webTheme ? localStorage.webTheme : "light");
  const [webLanguage, setWebLanguage] = useState(localStorage.webLanguage ? localStorage.webLanguage : "sk");
  // eslint-disable-next-line
  const [webText, setWebText] = useState(!localStorage.webLanguage ? slovakWebText : localStorage.webLanguage === "sk" ? slovakWebText : englishWebText);
  // eslint-disable-next-line
  const [ssoFetched, setSsoFetched] = useState(false);
  // eslint-disable-next-line
  const [userData, setUserData] = useState();

  const [authenticated, setAuthenticated] = useLocalStorage("authenticated", "false");


  useEffect(() => {

    const x = async () => {
      try {
        const data = await GET("getUserData");
        setUserData(data);
        handleAuthenticated();

      } catch (error) {
        setSsoFetched(true);
      };
    };

    x();

  // eslint-disable-next-line
  }, [])

  const toggleWebLanguage = () => {
      setWebLanguage(prevLanguage => (prevLanguage === "sk" ? "en" : "sk"));
      localStorage.webLanguage = !localStorage.webLanguage ? "en" : localStorage.webLanguage === "sk" ? "en" : "sk";
      window.location.reload();
  };

  const toggleTheme = () => {
      setWebTheme(prevTheme => (prevTheme === "light" ? "dark" : "light"));
      localStorage.webTheme = !localStorage.webTheme ? "dark" : localStorage.webTheme === "light" ? "dark" : "light";
  };

  const handleAuthenticated = () => {
      setAuthenticated(true);
      setSsoFetched(true);
  };

  const logMeOut = async () => {

      try {
          await POST("logout");

          enqueueSnackbar("Odhlásený", {
            variant: "success",
            autoHideDuration: 3000,
            onEntered: () => {
                setAuthenticated(false);
            }
          });

      } catch (error) {
          enqueueSnackbar(error, {
            variant: "error",
            autoHideDuration: 5000,
            onEntered: () => setAuthenticated(false)
          });
      };
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="unsuportedRess">
        <p>Toto rozlíšenie nieje podporované</p>
        <p>použite iné zariadenie.</p>
      </div>
      <div web-theme={webTheme} className="default">
          <BrowserRouter>
              {!ssoFetched ?
                  <Loading
                    toggleWebLanguage={toggleWebLanguage}
                    webLanguage={webLanguage}
                    toggleTheme={toggleTheme}
                    webTheme={webTheme}
                    webText={webText}
                    authenticated={authenticated}
                    logMeOut={logMeOut}
                  />
                  :
                  <Routes>
                      <Route element={<ProtectedRoutes isAuthenticated={authenticated}/>}>
                          <Route element={
                            <AddNewItem
                              toggleWebLanguage={toggleWebLanguage}
                              webLanguage={webLanguage}
                              toggleTheme={toggleTheme}
                              webTheme={webTheme}
                              webText={webText}
                              logMeOut={logMeOut}
                            />
                          } path="/addnewitem" exact/>
                          <Route element={
                            <MessageBox
                              toggleWebLanguage={toggleWebLanguage}
                              webLanguage={webLanguage}
                              toggleTheme={toggleTheme}
                              webTheme={webTheme}
                              webText={webText}
                              userData={userData}
                              logMeOut={logMeOut}
                            />
                          } path="/inbox" exact/>
                          <Route element={
                            <Edit
                              toggleWebLanguage={toggleWebLanguage}
                              webLanguage={webLanguage}
                              toggleTheme={toggleTheme}
                              webTheme={webTheme}
                              webText={webText}
                              logMeOut={logMeOut}
                            />
                          } path="/edit/:id" exact/>
                      </Route>
                      <Route element={
                        <Login
                          handleAuthenticated={handleAuthenticated}
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                        />
                      } path="/login" exact/>
                      <Route element={
                        <Register
                          handleAuthenticated={handleAuthenticated}
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                         />
                      } path="/register" exact/>
                      <Route element={
                        <Main
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                          logMeOut={logMeOut}
                        />
                      } path="/" exact/>
                      <Route element={
                        <Item
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                          logMeOut={logMeOut}
                        />
                      } path="/item/:id" exact/>
                      <Route element={
                        <Gdpr
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                          logMeOut={logMeOut}
                        />
                      } path="/gdpr" exact/>
                      <Route element={
                        <Contact
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                          logMeOut={logMeOut}
                        />
                      } path="/contact" exact/>
                      <Route element={
                        <NoPage
                          toggleWebLanguage={toggleWebLanguage}
                          webLanguage={webLanguage}
                          toggleTheme={toggleTheme}
                          webTheme={webTheme}
                          webText={webText}
                          logMeOut={logMeOut}
                        />
                      } path="*"/>
                  </Routes>
              }
              <Footer webTheme={webTheme}/>
          </BrowserRouter>
      </div>
    </ThemeProvider>
  );
};

export default App;